import { Chip, Tooltip, Box, Typography } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import type { RaRecord } from 'react-admin';
import { Link } from 'react-router-dom';
import { plNumber } from '../../shared/number';
import { dateTimeNoSeconds } from '../../shared/dateTime';
import { vehicleTypeLabel } from './vehicleTypes';

type LooseRecord = RaRecord & Record<string, unknown>;

const toText = (v: unknown): string | null =>
  typeof v === 'string' && v.trim() !== '' ? v : null;

/**
 * Dostępność razem z powodem i terminem.
 *
 * Samo „niedostępny" zmusza planistę do wejścia w szczegóły, żeby dowiedzieć
 * się, czy auto wróci jutro, czy za miesiąc.
 */
export const AvailabilityCell = ({ record }: { record: LooseRecord }) => {
  const available = record.available;
  const operational = record.operational;

  if (operational === false) {
    // „poza eksploatacją", nie „niesprawny": pole `vehicle_operational`
    // u klienta nie mówi o usterce. Z 331 tak oznaczonych pojazdów ŻADEN nie
    // ma przypisanego kierowcy, a wożą 236 zleceń wobec 5422 na pozostałych
    // — to wygląda na wycofanie z ruchu, nie na awarię. Twierdzenie
    // o usterce byłoby wnioskiem, którego dane nie niosą (pytanie 31).
    return <Chip size="small" color="default" variant="outlined" label="poza eksploatacją" />;
  }
  if (available === true) {
    return <Chip size="small" color="success" label="dostępny" />;
  }
  if (available === false) {
    const reason = toText(record.unavailableStatus);
    // Moment z TMS (w danych 08:00, 17:00) — z godziną, w czasie polskim.
    const until = record.unavailableTo
      ? dateTimeNoSeconds(String(record.unavailableTo))
      : null;
    const description = [reason, until && `do ${until}`].filter(Boolean).join(', ');
    return (
      <Tooltip title={description || 'bez podanego powodu'}>
        <Chip size="small" color="warning" label={description || 'niedostępny'} />
      </Tooltip>
    );
  }
  // Brak wartości to nie to samo co „niedostępny" — kartoteka klienta bywa
  // niewypełniona i nie zgadujemy za nią.
  return (
    <Typography variant="caption" color="text.secondary">
      brak danych
    </Typography>
  );
};

/**
 * Spółka właściciela — z widocznym brakiem.
 *
 * 124 pojazdy w kartotece klienta nie mają podanego właściciela. To dziura
 * w danych, nie „auto niczyje", więc planista ma ją zobaczyć.
 */
export const CompanyCell = ({ record }: { record: LooseRecord }) => {
  const f = record.company as LooseRecord | undefined;
  if (!f) {
    return (
      <Tooltip title="TMS nie podaje spółki, do której należy ten pojazd">
        <Chip
          size="small"
          variant="outlined"
          color="warning"
          icon={<WarningAmberIcon />}
          label="brak spółki"
        />
      </Tooltip>
    );
  }
  return (
    <Chip size="small" variant="outlined" label={String(f.country ?? f.name)} />
  );
};

/**
 * Kierowca przypisany po stronie klienta.
 *
 * Wypełniony przy 299 z 584 ciągników (51%), a przy naczepach nigdy. Pusto
 * jest więc tu zwyczajne, nie błędne — dlatego myślnik, nie ostrzeżenie.
 * Ostrzeżenie przy połowie floty planista przestanie czytać.
 */
export const DriverCell = ({ record }: { record: LooseRecord }) => {
  const k = record.driver as LooseRecord | undefined;
  if (!k) {
    return (
      <Typography variant="caption" color="text.secondary">
        —
      </Typography>
    );
  }
  return (
    <Typography variant="body2">
      {[k.name, k.surname].filter(Boolean).join(' ')}
    </Typography>
  );
};

/** Ładowność — dwa różne pola z ich kartoteki, pokazujemy to, co jest. */
export const PayloadCell = ({ record }: { record: LooseRecord }) => {
  const t = record.maxLoadTons;
  const kg = record.permissibleLoadCapacity;
  // Tony bywają ułamkowe — po polsku „24,5 t”, nie „24.5 t” (e2e, 28.09.2026).
  if (typeof t === 'number' && t > 0) return <span>{plNumber(t, 2)} t</span>;
  if (typeof kg === 'number' && kg > 0) return <span>{kg} kg</span>;
  return (
    <Typography variant="caption" color="text.secondary">
      —
    </Typography>
  );
};

/**
 * Wymiary pojazdu z kartoteki klienta — w MILIMETRACH.
 *
 * Nazwy kolumn (`lengthM`) przyjechały ze schematu FreightFusion i kłamią:
 * wartości z TMS klienta to 7700 × 2480 × 3000 przy masie całkowitej 18 t,
 * czyli zwykła przyczepa. W metrach byłoby to auto długie na 7,7 km.
 * Specyfikacja klienta jednostki nie podaje, więc zmierzyliśmy ją na
 * danych; do 22.09.2026 ekran podpisywał te liczby jako metry.
 *
 * Nie przeliczamy po cichu na metry — przelicznik byłby naszą interpretacją,
 * a to jest kartoteka klienta. Pokazujemy jego liczbę z jego jednostką.
 */
export function formatDimensions(record: Record<string, unknown>): string {
  const l = [record.lengthM, record.widthM, record.heightM];
  if (l.every((x) => x === null || x === undefined || x === '')) return '—';
  const part = (x: unknown) =>
    x === null || x === undefined || x === '' ? '?' : String(x);
  return `${part(l[0])} × ${part(l[1])} × ${part(l[2])} mm`;
}

/**
 * Druga część zestawu — odsyłacz, nie sam numer.
 *
 * Powiązanie przychodzi z TMS klienta (`related_vehicle_id`, wypełnione
 * w 99 z 300 pojazdów) i jest rozwiązywane po stronie API. Pojazd, którego
 * nie mamy w kartotece, pokazujemy jako brak — wypisanie surowego numeru
 * z ich systemu wyglądałoby jak link, którego nie da się otworzyć.
 */
export const LinkedVehicleCell = ({ record }: { record: LooseRecord }) => {
  const z = record.coupledVehicle as
    | { id: number; registrationNumber: string | null; carType: string | null }
    | null
    | undefined;
  if (!z) {
    return (
      <Typography variant="body2" color="text.secondary">
        {record.tmsRelatedVehicleId
          ? 'wskazany w TMS, ale nie ma go na liście pojazdów'
          : '—'}
      </Typography>
    );
  }
  const plate = toText(z.registrationNumber) ?? `#${z.id}`;
  const kind = toText(vehicleTypeLabel(z.carType));
  return (
    <Box>
      <Link to={`/cars/${z.id}/show`}>{plate}</Link>
      {kind && (
        <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
          {kind}
        </Typography>
      )}
    </Box>
  );
};

/**
 * Wózek przy pojeździe.
 *
 * Ma go 6 z 300 aut, a zlecenia potrafią go wymagać (`JUMBO TRUCK+FORKLIFT`
 * w kartotece zleceń), więc to rzadka cecha o realnym znaczeniu. Puste pole
 * w kartotece to **brak wiedzy**, nie „nie ma wózka".
 */
export const ForkliftCell = ({ record }: { record: LooseRecord }) => {
  if (record.forklift === true) return <Chip size="small" color="info" label="jest" />;
  if (record.forklift === false)
    return (
      <Typography variant="body2" color="text.secondary">
        brak
      </Typography>
    );
  return (
    <Typography variant="body2" color="text.secondary">
      brak danych
    </Typography>
  );
};
